import { z } from 'zod';
import { t } from '@budgeting101/i18n';

export const SharedGroupPurposeSchema = z.enum([
  'TRIP',
  'EVENT',
  'HOUSEHOLD',
  'PROJECT',
  'OTHER',
]);

export const SharedGroupStatusSchema = z.enum([
  'DRAFT',
  'ACTIVE',
  'COMPLETION_REVIEW',
  'ARCHIVED',
]);

export const SharedParticipantStatusSchema = z.enum([
  'ACTIVE',
  'INVITED',
  'LEFT',
  'ARCHIVED',
  'MERGED',
]);

export const SharedGroupSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  purpose: SharedGroupPurposeSchema,
  destination: z.string().nullable(),
  startsOn: z.string().date().nullable(),
  endsOn: z.string().date().nullable(),
  summaryCurrency: z.string().regex(/^[A-Z]{3}$/),
  timezone: z.string(),
  status: SharedGroupStatusSchema,
  version: z.number().int().positive(),
  archivedAt: z.string().datetime().nullable(),
}).strict();

export const SharedGroupDetailSchema = SharedGroupSummarySchema.extend({
  configEtag: z.string().regex(/^"shared-group-v1\.[0-9a-f]{64}"$/),
  viewerParticipantId: z.string().uuid().nullable(),
  viewerAccessVersion: z.number().int().positive().nullable(),
  effectivePermissions: z.array(z.string().regex(/^shared\.[a-z0-9.-]+$/)),
  participants: z.array(z.object({
    id: z.string().uuid(),
    kind: z.enum(['OWNER_MEMBER', 'MEMBER', 'ALIAS', 'GUEST']),
    displayName: z.string(),
    status: SharedParticipantStatusSchema,
  }).strict()),
}).strict();

export const SharedGroupListResponseSchema = z.object({
  items: z.array(SharedGroupSummarySchema),
  page: z.object({
    limit: z.number().int().min(1).max(50),
    scannedCount: z.number().int().nonnegative(),
    returnedCount: z.number().int().nonnegative(),
    hasMore: z.boolean(),
    nextCursor: z.string().min(1).max(512).nullable(),
  }).strict(),
}).strict();

const SharedGroupNameSchema = z.string().trim().min(1).max(120);
const ParticipantDisplayNameSchema = z.string().trim().min(1).max(120);
const NullableDestinationSchema = z.string().trim().min(1).max(160).nullable();
const CurrencySchema = z.string().regex(/^[A-Z]{3}$/);

export const SharedGroupDraftSchema = z.object({
  bookId: z.string().uuid(),
  name: SharedGroupNameSchema,
  purpose: SharedGroupPurposeSchema,
  destination: NullableDestinationSchema,
  startsOn: z.string().date().nullable(),
  endsOn: z.string().date().nullable(),
  summaryCurrency: CurrencySchema,
  timezone: z.string().min(1).max(255),
  organizerDisplayName: ParticipantDisplayNameSchema,
  participants: z.array(z.object({
    displayName: ParticipantDisplayNameSchema,
  }).strict()).min(1).max(199),
}).strict();

const SharedBlockerSchema = z.object({
  code: z.string().min(1).max(120),
  message: z.string().min(1).max(500),
}).strict();

export const SharedGroupCreatePreviewResponseSchema = z.object({
  contractVersion: z.literal(1),
  draft: SharedGroupDraftSchema,
  participantPlan: z.object({
    ownerDisplayName: ParticipantDisplayNameSchema,
    aliases: z.array(ParticipantDisplayNameSchema).min(1).max(199),
    total: z.number().int().min(2).max(200),
  }).strict(),
  readiness: z.object({
    state: z.enum(['READY', 'BLOCKED']),
    blockers: z.array(SharedBlockerSchema),
  }).strict(),
  preservedFacts: z.object({
    formalAccountingRecordsCreated: z.literal(false),
    economicFactsCreated: z.literal(false),
  }).strict(),
  previewFingerprint: z.string().regex(/^[0-9a-f]{64}$/),
}).strict();

export const SharedGroupCreateResponseSchema = z.object({
  group: SharedGroupDetailSchema,
  participantsCreated: z.number().int().min(2).max(200),
  formalAccountingRecordsCreated: z.literal(false),
  economicFactsCreated: z.literal(false),
  commandReceiptId: z.string().uuid(),
  correlationId: z.string().min(1).max(160),
}).strict();

export type SharedGroupSummary = z.infer<typeof SharedGroupSummarySchema>;
export type SharedGroupDetail = z.infer<typeof SharedGroupDetailSchema>;
export type SharedGroupDraft = z.infer<typeof SharedGroupDraftSchema>;
export type SharedGroupCreatePreview = z.infer<typeof SharedGroupCreatePreviewResponseSchema>;
export type SharedGroupCreateResponse = z.infer<typeof SharedGroupCreateResponseSchema>;

export const SHARED_PURPOSES = ['TRIP', 'EVENT', 'HOUSEHOLD', 'PROJECT', 'OTHER'] as const satisfies readonly SharedGroupSummary['purpose'][];

export function sharedPurposeLabel(purpose: SharedGroupSummary['purpose']): string {
  return t({
    TRIP: 'auth.purposeTrip',
    EVENT: 'auth.purposeEvent',
    HOUSEHOLD: 'auth.purposeHousehold',
    PROJECT: 'auth.purposeProject',
    OTHER: 'auth.purposeOther',
  }[purpose]);
}

export function sharedGroupStatusLabel(status: SharedGroupSummary['status']): string {
  return t({
    DRAFT: 'auth.statusDraft',
    ACTIVE: 'auth.statusActive',
    COMPLETION_REVIEW: 'auth.statusCompletionReview',
    ARCHIVED: 'auth.statusArchived',
  }[status]);
}

export function sharedParticipantStatusLabel(
  status: SharedGroupDetail['participants'][number]['status'],
): string {
  return t({
    ACTIVE: 'auth.statusActive',
    INVITED: 'auth.statusInvited',
    LEFT: 'sharedDetail.participantLeft',
    ARCHIVED: 'auth.statusArchived',
    MERGED: 'sharedDetail.participantMerged',
  }[status]);
}
